import type {FC} from 'react'
import {useState} from 'react'
import {
  Box,
  Button,
  Chip,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'

import {useCustomTranslation} from '@hooks/useCustomTranslation'
import type {DocumentSchema, SchemaTable} from '@interfaces/alpinist/document'

interface Props {
  schema: DocumentSchema
  /**
   * Name of the table the reader came here for — the sheet a citation names.
   * That one is shown first and marked; the rest fold away behind a count, so
   * a citation into sheet 8 of 24 does not open on sheet 1 with the cited sheet
   * two screens below the fold.
   */
  focus?: string
}

/**
 * What a queried document looks like (AB#5993).
 *
 * A spreadsheet or a PLC export is not read, it is queried — so showing
 * someone a page of its CSV tells them almost nothing, while showing them its
 * thirteen sheets, their columns and how those sheets join tells them what the
 * file *is*. That is also exactly what the agent is given before it writes a
 * query, so this panel and the answer are looking at the same thing.
 *
 * Two details are deliberately visible rather than tidied away. Every column's
 * `looks_like` is shown, because everything is stored as text and what the
 * values actually are is the one thing you cannot see from a type. And a
 * join's coverage is shown when it is not total: these are inferred from the
 * data rather than declared, and a join that describes 97% of the rows is
 * still the join — but the 3% is worth knowing about.
 */
const DocumentSchemaPanel: FC<Props> = ({schema, focus}) => {
  const [t] = useCustomTranslation('alpinist')
  const [othersOpen, setOthersOpen] = useState(false)
  // Hoisted because `t` is typed as TFunction, whose return is not narrowed to
  // string — an inline call in an aria-label does not typecheck.
  const joinsLabel: string = t('kb.schema.joins_label')

  const keysFor = (table: SchemaTable) =>
    schema.foreign_keys.filter((key) => key.table === table.name)

  const cited = focus ? schema.tables.find((table) => table.name === focus) : undefined
  // Unfocused, every table is shown: with nothing cited, the whole shape *is*
  // the answer to "what is this file".
  const leading = cited ? [cited] : schema.tables
  const folded = cited ? schema.tables.filter((table) => table !== cited) : []

  const renderTable = (table: SchemaTable) => {
    const keys = keysFor(table)
    return (
      <Box key={table.name}>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{mb: 0.5}}>
          <Typography variant="subtitle2" sx={{fontWeight: 600}}>
            {table.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('kb.schema.row_count', {count: table.rows})}
          </Typography>
          {table.truncated && (
            <Tooltip title={t('kb.schema.truncated_help')}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                icon={<WarningAmberOutlinedIcon />}
                label={t('kb.schema.truncated')}
              />
            </Tooltip>
          )}
        </Stack>

        <Box sx={{overflowX: 'auto'}}>
          <Table size="small" sx={{minWidth: 460}}>
            <TableHead>
              <TableRow>
                <TableCell>{t('kb.schema.column')}</TableCell>
                <TableCell>{t('kb.schema.looks_like')}</TableCell>
                <TableCell align="right">{t('kb.schema.distinct')}</TableCell>
                <TableCell>{t('kb.schema.samples')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {table.columns.map((column) => {
                const key = keys.find((k) => k.column === column.name)
                return (
                  <TableRow key={column.name} hover>
                    <TableCell sx={{fontFamily: 'monospace'}}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <span>{column.name}</span>
                        {key && (
                          <Tooltip
                            title={t('kb.schema.joins_help', {
                              table: key.references_table,
                              column: key.references_column,
                              percent: Math.round(key.coverage * 100),
                            })}
                          >
                            <KeyOutlinedIcon
                              fontSize="inherit"
                              color="primary"
                              aria-label={joinsLabel}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {column.looks_like}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {column.distinct.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          maxWidth: 260,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {column.samples.join(' · ')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>
    )
  }

  return (
    <Stack spacing={2.5}>
      {cited && (
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          label={t('kb.schema.cited_here')}
          sx={{alignSelf: 'flex-start'}}
        />
      )}
      {leading.map(renderTable)}
      {folded.length > 0 && (
        <Box>
          {/* The theme title-cases button labels, which turns a sentence into
              "The Other 12 Sheets In This File". */}
          <Button
            size="small"
            onClick={() => setOthersOpen((open) => !open)}
            sx={{textTransform: 'none'}}
          >
            {othersOpen
              ? t('kb.schema.hide_other_tables')
              : t('kb.schema.other_tables', {count: folded.length})}
          </Button>
          <Collapse in={othersOpen} unmountOnExit>
            <Stack spacing={2.5} sx={{mt: 1.5}}>
              {folded.map(renderTable)}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Stack>
  )
}

export default DocumentSchemaPanel
